import React from 'react';
import { Ship, Anchor, Train, Truck, CheckCircle, Clock, ArrowRight, MapPin } from 'lucide-react';

const STAGES = [
  { key: 'ocean', label: 'En Tránsito Marítimo', icon: Ship, color: 'blue' },
  { key: 'port', label: 'En Puerto', icon: Anchor, color: 'orange' },
  { key: 'rail', label: 'Transporte Terrestre (Tren)', icon: Train, color: 'violet' },
  { key: 'truck', label: 'Transporte Terrestre (Camión)', icon: Truck, color: 'emerald' },
];

const COLOR_MAP = {
  blue: { active: 'bg-blue-500 text-white shadow-blue-500/30', dot: 'bg-blue-500', line: 'bg-blue-500/60', muted: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  orange: { active: 'bg-orange-500 text-white shadow-orange-500/30', dot: 'bg-orange-500', line: 'bg-orange-500/60', muted: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  violet: { active: 'bg-violet-500 text-white shadow-violet-500/30', dot: 'bg-violet-500', line: 'bg-violet-500/60', muted: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
  emerald: { active: 'bg-emerald-500 text-white shadow-emerald-500/30', dot: 'bg-emerald-500', line: 'bg-emerald-500/60', muted: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
};

function resolveStage(container, carrierData, portData) {
  // Determine current stage based on tracking data
  const events = carrierData?.events || carrierData?.tracking_events || [];
  const portEvents = portData?.data || [];

  // Check if there's a delivery/truck event
  const hasDelivery = events.some(e => {
    const desc = (e.description || e.event || '').toLowerCase();
    return desc.includes('deliver') || desc.includes('entrega') || desc.includes('gate out') || desc.includes('empty return');
  });
  if (hasDelivery) return 3; // truck

  // Check for rail/inland transport
  const hasRail = events.some(e => {
    const desc = (e.description || e.event || '').toLowerCase();
    return desc.includes('rail') || desc.includes('tren') || desc.includes('ferrocarril') || desc.includes('inland');
  });
  if (hasRail) return 2; // rail

  // Check for port arrival
  const hasPortArrival = events.some(e => {
    const desc = (e.description || e.event || '').toLowerCase();
    return desc.includes('discharge') || desc.includes('descarga') || desc.includes('arrived') || desc.includes('arrival');
  }) || portEvents.length > 0;
  if (hasPortArrival) return 1; // port

  // Check for departure/sailing
  const hasSailing = events.some(e => {
    const desc = (e.description || e.event || '').toLowerCase();
    return desc.includes('depart') || desc.includes('sailed') || desc.includes('loaded') || desc.includes('cargado');
  });
  if (hasSailing) return 0; // ocean

  // Default: ocean if we have any tracking events, -1 if none
  return events.length > 0 ? 0 : -1;
}

function getStageDetails(stageIdx, container, carrierData, portData) {
  const events = carrierData?.events || carrierData?.tracking_events || [];
  const portEvents = portData?.data || [];

  switch (stageIdx) {
    case 0: { // ocean
      const departure = events.find(e => (e.description || e.event || '').toLowerCase().match(/depart|sailed|loaded/));
      return {
        location: departure?.location || container?.puerto_origen || '',
        date: departure?.date || departure?.timestamp || '',
        detail: departure ? (departure.description || departure.event) : 'Navegando',
      };
    }
    case 1: { // port
      if (portEvents.length > 0) {
        const last = portEvents[portEvents.length - 1];
        return {
          location: last.ubicacion || last.buque || container?.destino || 'Manzanillo',
          date: last.fecha_entrada || last.fecha_movimiento || '',
          detail: last.estatus || last.tipo_movimiento || 'En terminal',
        };
      }
      const arrival = events.find(e => (e.description || e.event || '').toLowerCase().match(/discharge|arrival|descarga/));
      return {
        location: arrival?.location || container?.destino || '',
        date: arrival?.date || arrival?.timestamp || '',
        detail: arrival ? (arrival.description || arrival.event) : '',
      };
    }
    case 2: { // rail
      const rail = events.find(e => (e.description || e.event || '').toLowerCase().match(/rail|tren|inland/));
      return {
        location: rail?.location || '',
        date: rail?.date || rail?.timestamp || '',
        detail: rail ? (rail.description || rail.event) : '',
      };
    }
    case 3: { // truck
      const delivery = events.find(e => (e.description || e.event || '').toLowerCase().match(/deliver|entrega|gate out/));
      return {
        location: delivery?.location || '',
        date: delivery?.date || delivery?.timestamp || '',
        detail: delivery ? (delivery.description || delivery.event) : '',
      };
    }
    default:
      return { location: '', date: '', detail: '' };
  }
}

export function TrackingTimeline({ container, carrierData, portData }) {
  const currentStage = resolveStage(container, carrierData, portData);

  return (
    <div className="p-3" data-testid={`timeline-${container?.container_number}`}>
      {/* Timeline bar */}
      <div className="flex items-center gap-0 mb-4">
        {STAGES.map((stage, idx) => {
          const colors = COLOR_MAP[stage.color];
          const isActive = idx === currentStage;
          const isDone = idx < currentStage;
          const isPending = idx > currentStage;
          const Icon = stage.icon;

          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center flex-1 min-w-0">
                {/* Icon circle */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                    isActive
                      ? `${colors.active} shadow-lg ring-2 ring-offset-2 ring-offset-background ring-current animate-pulse`
                      : isDone
                      ? `${colors.dot} text-white opacity-80`
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`timeline-stage-${stage.key}`}
                >
                  {isDone ? <CheckCircle size={18} /> : <Icon size={18} />}
                </div>
                {/* Label */}
                <span className={`text-[10px] mt-1.5 text-center leading-tight max-w-[80px] ${isActive ? 'font-semibold text-foreground' : isDone ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>
                  {stage.label}
                </span>
              </div>
              {/* Connector line */}
              {idx < STAGES.length - 1 && (
                <div className="flex-shrink-0 w-8 sm:w-12 flex items-center -mt-4">
                  <div className={`h-0.5 w-full rounded-full transition-all duration-500 ${
                    idx < currentStage ? colors.line : 'bg-border'
                  }`} />
                  <ArrowRight size={10} className={`-ml-1 flex-shrink-0 ${idx < currentStage ? colors.line.replace('bg-', 'text-') : 'text-border'}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Stage detail cards */}
      {currentStage >= 0 && (
        <div className="grid grid-cols-4 gap-2">
          {STAGES.map((stage, idx) => {
            const colors = COLOR_MAP[stage.color];
            const isActive = idx === currentStage;
            const isDone = idx < currentStage;
            if (idx > currentStage) return <div key={stage.key} />;

            const details = getStageDetails(idx, container, carrierData, portData);

            return (
              <div
                key={stage.key}
                className={`rounded-lg border p-2 text-xs transition-all ${
                  isActive ? `${colors.muted} border` : isDone ? 'bg-muted/30 border-border/50' : ''
                }`}
              >
                {details.location && (
                  <div className="flex items-center gap-1 mb-0.5">
                    <MapPin size={9} className="text-muted-foreground flex-shrink-0" />
                    <span className="font-medium truncate">{details.location}</span>
                  </div>
                )}
                {details.date && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock size={9} className="flex-shrink-0" />
                    <span className="truncate">{typeof details.date === 'string' ? details.date.slice(0, 16) : ''}</span>
                  </div>
                )}
                {details.detail && (
                  <p className="text-muted-foreground truncate mt-0.5">{details.detail}</p>
                )}
                {!details.location && !details.date && !details.detail && (
                  <span className="text-muted-foreground/50 italic">Sin datos</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {currentStage < 0 && (
        <p className="text-xs text-muted-foreground/50 text-center py-2 italic">
          Sin datos de tracking. Consulta la naviera o terminal para actualizar.
        </p>
      )}
    </div>
  );
}
